import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { api } from '@/api';
import { GameCard } from '@/components/common/GameCard';
import { PageHeroBanner } from '@/components/common/PageHeroBanner';
import { SEO } from '@/components/common/SEO';

export const SportPage = ({ sports }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [sport, setSport] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [games, setGames] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [engagementScores, setEngagementScores] = useState({});

  useEffect(() => {
    const loadData = async () => {
      const foundSport = sports.find(s => s.slug === slug);
      if (!foundSport) return;
      setSport(foundSport);

      try {
        const [leaguesRes, gamesRes] = await Promise.all([
          api.getLeagues(foundSport.id),
          api.getGames({ sport_id: foundSport.id })
        ]);
        setLeagues(leaguesRes.data);
        setGames(gamesRes.data);
        try { const e = await api.getEngagementScores(); setEngagementScores(e.data.scores || {}); } catch {}
      } catch (error) {
        console.error('Error loading sport data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [slug, sports]);

  const filteredGames = selectedLeague 
    ? games.filter(g => g.league_id === selectedLeague)
    : games;

  if (!sport) return <div className="p-8 text-center text-gray-400">Sport not found</div>;

  return (
    <div data-testid={`sport-page-${slug}`}>
      <SEO 
        title={sport.name}
        description={`Watch live ${sport.name} matches and get scores. Browse leagues, teams, and upcoming games in the United States.`}
        url={`/sport/${slug}`}
      />
      <PageHeroBanner 
        pageSlug={`sport-${slug}`} 
        fallbackTitle={sport.name}
        fallbackSubtitle={`Browse all ${sport.name} leagues and games`}
      />
      
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* League Filter - Horizontal Scrollable */}
          <div className="mb-8 -mx-4 px-4">
            <div 
              className="flex gap-3 pb-4 overflow-x-auto scrollbar-hide"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              <Button
                variant={selectedLeague === null ? "default" : "outline"}
                className={`flex-shrink-0 ${selectedLeague === null ? "bg-sky-300" : "border-sky-400 text-sky-100"}`}
                onClick={() => setSelectedLeague(null)}
                data-testid="filter-all-leagues"
              >
                All Leagues
              </Button>
              {leagues.map(league => (
                <Button
                  key={league.id}
                  variant={selectedLeague === league.id ? "default" : "outline"}
                  className={`flex-shrink-0 ${selectedLeague === league.id ? "bg-sky-300" : "border-sky-400 text-sky-100"}`}
                  onClick={() => setSelectedLeague(league.id)}
                  data-testid={`filter-league-${league.slug}`}
                >
                  {league.name}
                </Button>
              ))}
            </div>
          </div>

          {/* Games Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="game-card animate-pulse">
                  <div className="aspect-video bg-gray-700 rounded-t-xl" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-gray-700 rounded w-1/2" />
                    <div className="h-5 bg-gray-700 rounded w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredGames.map(game => (
                <GameCard key={game.id} game={game} onWatch={() => navigate(`/watch/${game.id}`)} engagementScore={engagementScores[game.id]?.score || 0} />
              ))}
              {filteredGames.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-400">No games found for this selection.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default SportPage;
